import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  saveToLocal,
  getFromLocal,
  OFFLINE_KEYS,
  removeFromLocal,
} from "@/utils/offlineStorage";
import { apiFetch } from "@/service/api";

export const useOfflineSync = () => {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);

  const downloadInfo = async () => {
    if (!user?.id) return;
    setSyncing(true);
    try {
      const tareasRes = await apiFetch(
        `empleados-proyectos/find-tareas/${user.id}`,
      );
      if (tareasRes.ok) {
        const tareas = await tareasRes.json();
        saveToLocal(OFFLINE_KEYS.TAREAS, tareas);
      }
      const encuestasRes = await apiFetch(
        `encuesta-usuario-proyecto/find-by-user/${user.id}`,
      );
      if (encuestasRes.ok) {
        const encuestas = await encuestasRes.json();
        saveToLocal(OFFLINE_KEYS.ENCUESTAS, encuestas);
        for (const item of encuestas) {
          if (item.encuesta?.id) {
            try {
              const sRes = await apiFetch(
                `survey/find-response/${item.encuesta.id}`,
              );
              if (sRes.ok) {
                const surveyData = await sRes.json();
                if (typeof surveyData.survey === "string") {
                  try {
                    surveyData.survey = JSON.parse(surveyData.survey);
                  } catch (e) {
                    console.error("Error parsing survey JSON:", e);
                  }
                }
                saveToLocal(`survey_detail_${item.encuesta.id}`, surveyData);
              }
            } catch (err) {
              console.error(
                `Error downloading survey ${item.encuesta.id}:`,
                err,
              );
            }
          }
        }
      }
      alert("Informacion descargada correctamente para uso offline.");
    } catch (error) {
      console.error("Error downloading offline info:", error);
      alert("Error al descargar informacion.");
    } finally {
      setSyncing(false);
    }
  };

  const uploadInfo = async () => {
    const pendingResponses = getFromLocal(OFFLINE_KEYS.PENDING_RESPONSES) || [];
    const pendingCoords = getFromLocal(OFFLINE_KEYS.PENDING_COORDINATES) || [];
    const pendingTasks = getFromLocal(OFFLINE_KEYS.PENDING_TASKS) || [];
    if (
      pendingResponses.length === 0 &&
      pendingCoords.length === 0 &&
      pendingTasks.length === 0
    ) {
      alert("No hay informacion pendiente por subir.");
      return;
    }
    setSyncing(true);
    try {
      for (const item of pendingResponses) {
        const response = await apiFetch(`survey-response/create`, {
          method: "POST",
          body: JSON.stringify(item.data),
        });
        if (response.ok) {
          const savedResponse = await response.json();
          const coords = pendingCoords.find(
            (c: any) => c.local_id === item.local_id,
          );
          if (coords) {
            const coordsRes = await apiFetch(`coordenadas-respuesta/create`, {
              method: "POST",
              body: JSON.stringify({
                ...coords.data,
                survey_response_id: savedResponse.id,
              }),
            });
            if (!coordsRes.ok)
              console.error(
                "Error syncing coordinates:",
                await coordsRes.json(),
              );
          }
        } else {
          console.error(
            "Error syncing survey response:",
            await response.json(),
          );
        }
      }
      for (const task of pendingTasks) {
        await apiFetch(`empleados-proyectos/update/${task.id}`, {
          method: "PATCH",
          body: JSON.stringify({ estado_tarea: task.estado_tarea }),
        });
      }
      removeFromLocal(OFFLINE_KEYS.PENDING_RESPONSES);
      removeFromLocal(OFFLINE_KEYS.PENDING_COORDINATES);
      removeFromLocal(OFFLINE_KEYS.PENDING_TASKS);
      alert("Informacion sincronizada correctamente.");
    } catch (error) {
      console.error("Error uploading offline info:", error);
      alert("Error al subir informacion.");
    } finally {
      setSyncing(false);
    }
  };

  return { downloadInfo, uploadInfo, syncing };
};
